import axios from 'axios';
import { adminConfig } from '../config/firebase-config';

export interface Rating {
  id: string;
  conversationId: string;
  messageId?: string;
  rating: 'good' | 'bad' | 'wrong';
  notes?: string;
  timestamp: Date;
  adminEmail: string;
  resolved?: boolean;
  context?: {
    userMessage: string;
    aiResponse: string;
  };
}

export interface RatingAnalytics {
  totalRatings: number;
  distribution: {
    good: number;
    bad: number;
    wrong: number;
  };
  ratingTrends: Array<{
    date: string;
    good: number;
    bad: number;
    wrong: number;
  }>;
  recentRatings: Array<{
    id: string;
    conversationId: string;
    rating: 'good' | 'bad' | 'wrong';
    notes?: string;
    timestamp: Date;
  }>;
}

class RatingsService {
  private getAuthHeaders() {
    return {
      'Content-Type': 'application/json',
      'Authorization': 'geoff@emertech.nz' // TODO: Replace with real auth
    };
  }

  async getAllRatings(limit: number = 50, offset: number = 0): Promise<{ ratings: Rating[]; total: number }> {
    try {
      const response = await axios.get(`${adminConfig.apiBaseUrl}${adminConfig.endpoints.ratings}`, {
        params: { limit, offset },
        headers: this.getAuthHeaders()
      });
      
      return {
        ratings: response.data.ratings.map((rating: any) => ({
          ...rating,
          timestamp: rating.timestamp ? new Date(rating.timestamp) : new Date() // Parse ISO date string from API, fallback to now
        })),
        total: response.data.total
      };
    } catch (error) {
      console.error('Error fetching ratings:', error);
      throw error;
    }
  }

  async getRatingAnalytics(startDate?: Date, endDate?: Date): Promise<RatingAnalytics> {
    try {
      const params: any = {};
      if (startDate) params.startDate = startDate.toISOString();
      if (endDate) params.endDate = endDate.toISOString();

      const response = await axios.get(`${adminConfig.apiBaseUrl}${adminConfig.endpoints.ratingAnalytics}`, {
        params,
        headers: this.getAuthHeaders()
      });
      
      const data = response.data;
      return {
        ...data,
        // Map backend field names to frontend interface
        distribution: data.ratingDistribution || { good: 0, bad: 0, wrong: 0 },
        recentRatings: data.recentRatings.map((rating: any) => ({
          ...rating,
          timestamp: rating.timestamp ? new Date(rating.timestamp) : new Date() // Parse ISO date string from API, fallback to now
        }))
      };
    } catch (error) {
      console.error('Error fetching rating analytics:', error);
      throw error;
    }
  }

  async searchRatings(query: string, ratingType?: 'good' | 'bad' | 'wrong', limit: number = 50): Promise<Rating[]> {
    try {
      const params: any = { search: query, limit };
      if (ratingType) params.rating = ratingType;

      const response = await axios.get(`${adminConfig.apiBaseUrl}${adminConfig.endpoints.ratings}`, {
        params,
        headers: this.getAuthHeaders()
      });
      
      return response.data.ratings.map((rating: any) => ({
        ...rating,
        timestamp: rating.timestamp ? new Date(rating.timestamp) : new Date() // Parse ISO date string from API, fallback to now
      }));
    } catch (error) {
      console.error('Error searching ratings:', error);
      throw error;
    }
  }

  async getRatingsForConversation(conversationId: string): Promise<Rating[]> {
    try {
      const response = await axios.get(`${adminConfig.apiBaseUrl}${adminConfig.endpoints.ratings}`, {
        params: { conversationId },
        headers: this.getAuthHeaders()
      });
      
      return response.data.ratings.map((rating: any) => ({
        ...rating,
        timestamp: rating.timestamp ? new Date(rating.timestamp) : new Date() // Parse ISO date string from API, fallback to now
      }));
    } catch (error) {
      console.error('Error fetching ratings for conversation:', error);
      throw error;
    }
  }

  async markResolved(ratingId: string, resolved: boolean = true): Promise<void> {
    try {
      await axios.put(`${adminConfig.apiBaseUrl}/updateRating`, {
        id: ratingId,
        resolved: resolved
      }, {
        headers: this.getAuthHeaders()
      });
    } catch (error) {
      console.error('Error marking rating as resolved:', error);
      throw error;
    }
  }
}

export const ratingsService = new RatingsService();
